import { useState, useEffect } from 'react';
import devLog from '../utils/devLog';

/**
 * Detects the browser name from the user-agent string.
 */
const detectBrowser = () => {
  /* istanbul ignore next -- SSR guard, navigator always defined in jsdom */
  if (typeof navigator === 'undefined') return 'Unknown';
  const { userAgent } = navigator;
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Firefox')) return 'Firefox';
  return 'Unknown';
};

/**
 * Detects the browser version from the user-agent string.
 */
const detectBrowserVersion = () => {
  /* istanbul ignore next -- SSR guard */
  if (typeof navigator === 'undefined') return '';
  const { userAgent } = navigator;
  const match = userAgent.match(/(Chrome|Edg|Firefox|Version)\/(\d+)/);
  return match ? match[2] : '';
};

/**
 * Detects the operating system from the user-agent string.
 */
const detectOperatingSystem = () => {
  /* istanbul ignore next -- SSR guard */
  if (typeof navigator === 'undefined') return 'Unknown';
  const { userAgent } = navigator;
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
  if (/Android/.test(userAgent)) return 'Android';
  if (/Mac OS X/.test(userAgent)) return 'macOS';
  if (/Windows/.test(userAgent)) return 'Windows';
  if (/Linux/.test(userAgent)) return 'Linux';
  return 'Unknown';
};

/**
 * Detects the device type from the user-agent string.
 */
const detectDeviceType = () => {
  /* istanbul ignore next -- SSR guard */
  if (typeof navigator === 'undefined') return 'DESKTOP';
  const { userAgent } = navigator;
  if (/iPhone|Android.*Mobile/.test(userAgent)) return 'MOBILE';
  if (/iPad|Android(?!.*Mobile)/.test(userAgent)) return 'TABLET';
  return 'DESKTOP';
};

/**
 * Checks SPC and BBK support using the dedicated browser APIs:
 *
 *   1. PaymentRequest.securePaymentConfirmationAvailability()
 *      – authoritative SPC availability signal; returns "available" | reason-string.
 *   2. PaymentRequest.getSecurePaymentConfirmationCapabilities()  (experimental)
 *      – exposes whether the platform has hardware-bound BBK support.
 *      When the browser does not implement this method bbkAvailable is null
 *      (unknown) rather than false (definitely unavailable).
 *
 * @returns {{
 *   spcAvailable: boolean,
 *   bbkAvailable: boolean | null,
 *   isSpcBbkLoading: boolean,
 *   reason: string | undefined,
 *   deviceContext: {
 *     browser: string, browserVersion: string, operatingSystem: string,
 *     deviceType: string, spcSupported: boolean, webauthnSupported: boolean
 *   }
 * }}
 */
const useSpcBbkCompatibility = () => {
  const [status, setStatus] = useState({
    spcAvailable: false,
    bbkAvailable: null,
    isSpcBbkLoading: true,
    reason: undefined,
    deviceContext: null,
  });

  useEffect(() => {
    let cancelled = false;

    const safeSetStatus = (next) => {
      if (!cancelled) setStatus(next);
    };

    const webauthnSupported = typeof window !== 'undefined'
      && typeof window.PublicKeyCredential !== 'undefined';

    const deviceContext = {
      browser: detectBrowser(),
      browserVersion: detectBrowserVersion(),
      operatingSystem: detectOperatingSystem(),
      deviceType: detectDeviceType(),
      spcSupported: false,
      webauthnSupported,
    };

    const setUnavailable = (reason) => safeSetStatus({
      spcAvailable: false,
      bbkAvailable: null,
      isSpcBbkLoading: false,
      reason,
      deviceContext: { ...deviceContext, spcSupported: false },
    });

    const setAvailable = (bbkAvailable) => safeSetStatus({
      spcAvailable: true,
      bbkAvailable,
      isSpcBbkLoading: false,
      reason: undefined,
      deviceContext: { ...deviceContext, spcSupported: true },
    });

    const checkCompatibility = async () => {
      // Guard: API not present in this browser build
      if (typeof window === 'undefined'
        || typeof window.PaymentRequest?.securePaymentConfirmationAvailability !== 'function') {
        setUnavailable('PaymentRequest SPC API missing');
        return;
      }

      const spcResult = await window.PaymentRequest.securePaymentConfirmationAvailability();

      if (spcResult !== 'available') {
        setUnavailable(spcResult);
        return;
      }

      // Experimental BBK capability check — may not be present yet
      if (typeof window.PaymentRequest.getSecurePaymentConfirmationCapabilities === 'function') {
        const caps = await window.PaymentRequest.getSecurePaymentConfirmationCapabilities();
        setAvailable(Boolean(caps?.browserBoundKeyHardware));
      } else {
        // SPC is available but browser does not expose BBK capability info
        setAvailable(null);
      }
    };

    checkCompatibility().catch((err) => {
      devLog.error('SPC/BBK compatibility check failed:', err);
      setUnavailable('check failed');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return status;
};

export default useSpcBbkCompatibility;
